import { SLIDESHOW_DEFAULTS } from 'ts/common/components/slideshows/slideshowDefaults';

/**
 * NLE-style overlap timeline: adjacent segments share an overlap window of length T
 * at each boundary. Segment i starts at start[i]; overlap between i and i+1 is
 * [start[i+1], start[i+1] + T).
 */

export interface OverlapTimelineLayout {
    segmentStarts: number[];
    segmentDurations: number[];
    /** Effective overlap used (may be clamped from requested transition duration) */
    overlapDuration: number;
    /** Effective overlap at each slide boundary. */
    boundaryOverlapDurations: number[];
    totalDuration: number;
}

export interface ActiveSlidesAtTime {
    /** Outgoing slide index during overlap; sole slide when secondary is null */
    primaryIndex: number;
    /** Incoming slide index during overlap */
    secondaryIndex: number | null;
    /** 0 = outgoing only, 1 = incoming only (linear crossfade weight for incoming) */
    blend: number;
}

export interface SlideshowTransitionConfig {
    type: SpApi.SlideshowTransition;
    duration: number;
}

/**
 * Minimum shape required by the slideshow player and its helper functions.
 * Both SpApi.IEventSlideshow (studio) and SpApi.Client.IEventSlideshow (client)
 * satisfy this interface structurally — no cast or explicit inheritance needed.
 */
export interface ISlideshow {
    aspectRatio: string | null;
    startTransition: SpApi.SlideshowTransition | null;
    startTransitionDuration: number | null;
    slides: SpApi.ISlideshowSlide[];
}

export function getSlideDurationMs(slide: SpApi.ISlideshowSlide): number {
    return slide.duration ?? SLIDESHOW_DEFAULTS.slide.duration;
}

function getTransitionDurationMs(duration: number | null | undefined): number {
    return duration ?? SLIDESHOW_DEFAULTS.slide.transitionDuration;
}

export function getSlideTransition(
    slide: SpApi.ISlideshowSlide,
    slideshow: ISlideshow
): SlideshowTransitionConfig {
    const type: SpApi.SlideshowTransition =
        slide.transition ?? slideshow.startTransition ?? SLIDESHOW_DEFAULTS.slide.transition;
    const duration = slide.transitionDuration ?? slideshow.startTransitionDuration;

    return {
        type,
        duration: type === 'cut' ? 0 : getTransitionDurationMs(duration)
    };
}

export function getBoundaryTransitionDurations(slideshow: ISlideshow): number[] {
    return slideshow.slides.slice(0, -1).map((slide, index) => {
        const requestedDuration = getSlideTransition(slide, slideshow).duration;
        const currentDuration = getSlideDurationMs(slide);
        const nextDuration = getSlideDurationMs(slideshow.slides[index + 1]);

        return clampOverlapToSegmentBoundary(requestedDuration, currentDuration, nextDuration);
    });
}

export function getSlideSeekTimes(slideshow: ISlideshow): number[] {
    const layout = buildOverlapTimeline(
        slideshow.slides.map(getSlideDurationMs),
        getBoundaryTransitionDurations(slideshow)
    );

    return layout.segmentStarts.map((start, index) => {
        if (index === 0) {
            return start;
        }

        return start + layout.boundaryOverlapDurations[index - 1];
    });
}

/**
 * Builds segment start times for uniform overlap between consecutive slides.
 * start[i+1] = start[i] + D[i] - T
 * Total duration = start[n-1] + D[n-1] = sum(D) - (n-1)*T
 */
export function buildOverlapTimeline(
    segmentDurations: number[],
    requestedOverlapDuration: number | number[]
): OverlapTimelineLayout {
    const n = segmentDurations.length;

    const boundaryOverlapDurations = Array.isArray(requestedOverlapDuration)
        ? clampBoundaryOverlapsToSegments(segmentDurations, requestedOverlapDuration)
        : new Array(Math.max(0, n - 1)).fill(
              clampOverlapToSegments(segmentDurations, requestedOverlapDuration)
          );
    const overlapDuration = Math.max(0, ...boundaryOverlapDurations);

    if (n === 0) {
        return {
            segmentStarts: [],
            segmentDurations: [],
            overlapDuration,
            boundaryOverlapDurations,
            totalDuration: 0
        };
    }

    if (n === 1) {
        return {
            segmentStarts: [0],
            segmentDurations: [...segmentDurations],
            overlapDuration,
            boundaryOverlapDurations,
            totalDuration: segmentDurations[0]
        };
    }

    const starts: number[] = [0];

    for (let i = 1; i < n; i++) {
        starts.push(starts[i - 1] + segmentDurations[i - 1] - boundaryOverlapDurations[i - 1]);
    }

    const totalDuration = starts[n - 1] + segmentDurations[n - 1];

    return {
        segmentStarts: starts,
        segmentDurations: [...segmentDurations],
        overlapDuration,
        boundaryOverlapDurations,
        totalDuration
    };
}

/** Ensures adjacent transition windows cannot overlap within a segment. */
export function clampOverlapToSegments(
    segmentDurations: number[],
    overlapDuration: number
): number {
    const n = segmentDurations.length;

    if (n <= 1 || overlapDuration <= 0) {
        return 0;
    }

    let maxT = overlapDuration;

    for (let i = 0; i < n - 1; i++) {
        maxT = clampOverlapToSegmentBoundary(maxT, segmentDurations[i], segmentDurations[i + 1]);
    }

    return Math.max(0, maxT);
}

function clampOverlapToSegmentBoundary(
    overlapDuration: number,
    currentDuration: number,
    nextDuration: number
): number {
    const maxOverlap = Math.max(0, Math.min(currentDuration, nextDuration) / 2);

    return Math.max(0, Math.min(overlapDuration, maxOverlap));
}

function clampBoundaryOverlapsToSegments(
    segmentDurations: number[],
    overlapDurationsByBoundary: number[]
): number[] {
    return segmentDurations.slice(0, -1).map((duration, index) => {
        const requestedOverlap = overlapDurationsByBoundary[index] ?? 0;

        return clampOverlapToSegmentBoundary(
            requestedOverlap,
            duration,
            segmentDurations[index + 1]
        );
    });
}

export function getActiveSlidesAtTime(
    time: number,
    layout: OverlapTimelineLayout
): ActiveSlidesAtTime {
    const {
        segmentStarts: starts,
        segmentDurations: D,
        overlapDuration: uniformOverlapDuration,
        boundaryOverlapDurations,
        totalDuration
    } = layout;
    const n = D.length;

    if (n === 0) {
        return { primaryIndex: 0, secondaryIndex: null, blend: 0 };
    }

    const t = wrapTime(time, totalDuration);

    for (let i = 0; i < n - 1; i++) {
        const overlapStart = starts[i + 1];

        if (t < overlapStart) {
            return { primaryIndex: i, secondaryIndex: null, blend: 0 };
        }

        const overlapDuration = boundaryOverlapDurations[i] ?? uniformOverlapDuration;

        if (overlapDuration > 0 && t < overlapStart + overlapDuration) {
            return {
                primaryIndex: i,
                secondaryIndex: i + 1,
                blend: Math.min(1, Math.max(0, (t - overlapStart) / overlapDuration))
            };
        }
    }

    return { primaryIndex: n - 1, secondaryIndex: null, blend: 0 };
}

function wrapTime(t: number, totalDuration: number): number {
    if (totalDuration <= 0) {
        return 0;
    }

    let wrapped = t % totalDuration;

    if (wrapped < 0) {
        wrapped += totalDuration;
    }

    return wrapped;
}

/** Total duration with overlap formula: sum(D) - (n-1)*T */
export function computeTotalDurationOverlap(
    segmentDurations: number[],
    overlapDuration: number,
    cutTransition: boolean
): number {
    const n = segmentDurations.length;

    if (n === 0) {
        return 0;
    }

    const effectiveOverlapDuration = cutTransition ? 0 : overlapDuration;

    return buildOverlapTimeline(segmentDurations, effectiveOverlapDuration).totalDuration;
}
